// ----------------------------------------------------------------------------
// UploadSongModal — choose a rehearsal recording (MP3, WAV or M4A), name it and
// start the analysis. At the 20-song limit it asks which song to replace,
// oldest first and preselected. Orchestration lives in useChordLibrary.
// ----------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type { ChordUpload } from '@app/api/chords';
import { CHORD_LIBRARY_SONG_LIMIT } from '@app/api/chords';
import { Banner, DatePicker, Field, Input, Modal, ModalActions, Select, cx } from '@app/components/ui';
import { monthDay } from '@app/lib/format';
import { ensureNotificationPermission } from '../../context/ChordWatchProvider';
import { STUDIO_ACCEPT, STUDIO_FORMAT_ERROR, deriveTitle, isStudioAudio } from './studioFiles';

interface UploadSongModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Returns a result the caller can react to (e.g. show an upload indicator);
  // the value is otherwise ignored here.
  onUpload: (file: File, title: string, keyHint?: string, replaceSourceId?: string, artist?: string) => Promise<unknown>;
  // Optional pre-selected file (e.g. handed in from a drag-and-drop drop zone).
  initialFile?: File | null;
  existingUploads?: ChordUpload[];
  maxSongs?: number;
}

const KEY_HINTS = ['C', 'C#', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B', 'Am', 'Bm', 'Cm', 'Dm', 'Em', 'F#m', 'Gm'];

const fileSize = (n: number) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const UploadSongModal: React.FC<UploadSongModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  initialFile,
  existingUploads = [],
  maxSongs = CHORD_LIBRARY_SONG_LIMIT,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [rehearsal, setRehearsal] = useState<Date | null>(() => new Date());
  const [keyHint, setKeyHint] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedReplaceId, setSelectedReplaceId] = useState<string>('');
  const fileRef = useRef<HTMLInputElement>(null);

  // Count real non-temp uploads
  const savedSongs = useMemo(() => existingUploads.filter((u) => !u.sourceId.startsWith('temp-')), [existingUploads]);
  const isAtLimit = savedSongs.length >= maxSongs;

  // Oldest first; the first one is the recommended replacement.
  const byAge = useMemo(
    () =>
      [...savedSongs].sort((a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return timeA - timeB;
      }),
    [savedSongs],
  );
  const oldestSong = byAge[0];

  // Adopt a pre-selected file when the modal is opened from a drop zone.
  useEffect(() => {
    if (isOpen && initialFile) {
      if (isStudioAudio(initialFile)) {
        setFile(initialFile);
        setError(null);
      } else {
        setError(STUDIO_FORMAT_ERROR);
      }
    }
  }, [isOpen, initialFile]);

  useEffect(() => {
    if (isOpen) setSelectedReplaceId(isAtLimit && oldestSong ? oldestSong.sourceId : '');
  }, [isOpen, isAtLimit, oldestSong?.sourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => {
    setFile(null);
    setTitle('');
    setArtist('');
    setRehearsal(new Date());
    setKeyHint('');
    setError(null);
    setSelectedReplaceId('');
  };

  const close = () => {
    reset();
    onClose();
  };

  const pick = (f: File | null | undefined) => {
    if (!f) return;
    if (!isStudioAudio(f)) {
      setError(STUDIO_FORMAT_ERROR);
      return;
    }
    setFile(f);
    setError(null);
  };

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!file) {
      setError('Please choose an audio file.');
      return;
    }
    if (isAtLimit && !selectedReplaceId) {
      setError('Please select a song to replace.');
      return;
    }
    const effectiveTitle = title.trim() || deriveTitle(file.name);
    // Ask for notification permission on this gesture so we can alert the admin
    // when the background analysis finishes.
    ensureNotificationPermission();
    // Hand off to the background; the modal closes right away. Validation,
    // conversion, upload and analysis all happen in useChordLibrary.
    // The rehearsal date stays on this form: the upload record has no field for it.
    void onUpload(file, effectiveTitle, keyHint || undefined, isAtLimit ? selectedReplaceId : undefined, artist.trim() || undefined);
    close();
  };

  return (
    <Modal
      open={isOpen}
      onClose={close}
      align="top"
      width={520}
      title={isAtLimit ? 'Replace a song in Song Studio' : 'Upload to Song Studio'}
      subtitle="Rehearsal recording · MP3, WAV or M4A"
    >
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <input
          ref={fileRef}
          type="file"
          accept={STUDIO_ACCEPT}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => pick(e.target.files?.[0])}
        />

        {isAtLimit && (
          <div className="p-3.5 rounded-[14px] bg-amb-bg flex flex-col gap-2.5">
            <div className="text-[13.5px] leading-normal">
              <div className="font-semibold">
                Library limit reached ({savedSongs.length}/{maxSongs} songs)
              </div>
              <div className="text-t2">Choose a song to replace. The oldest is recommended.</div>
            </div>
            <div>
              <label id="replace-song-label" htmlFor="replace-song-select" className="wp-label !mb-1.5">
                Song to replace
              </label>
              <Select
                id="replace-song-select"
                aria-labelledby="replace-song-label"
                value={selectedReplaceId}
                onChange={setSelectedReplaceId}
                options={byAge.map((s, i) => ({
                  value: s.sourceId,
                  label: s.title,
                  sub: s.createdAt ? `Added ${monthDay(s.createdAt)}${s.artist ? ` · ${s.artist}` : ''}` : s.artist,
                  tag: i === 0 ? 'Oldest' : undefined,
                }))}
              />
            </div>
            <div className="text-[12.5px] text-t2 leading-normal">
              The selected song's audio, separated stems and records are permanently deleted, then your new song is uploaded in its place.
            </div>
          </div>
        )}

        <div>
          <span className="wp-label">Audio file</span>
          {file ? (
            <div className="flex items-center gap-3 px-3.5 py-3 rounded-[14px] bg-fill border border-line2">
              <span className="w-9 h-9 rounded-[10px] bg-acc-bg2 text-acc flex items-center justify-center text-[15px] shrink-0" aria-hidden="true">
                ♪
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[14px] font-semibold truncate">{file.name}</span>
                <span className="block text-[12.5px] text-t4">{fileSize(file.size)}</span>
              </span>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="h-8 px-3 rounded-full border border-line2 bg-card text-t2 text-[12.5px] shrink-0 hover:text-t1"
              >
                Change
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className={cx(
                'w-full h-[72px] rounded-[14px] border-[1.5px] border-dashed bg-fill text-t2 text-[14px] font-medium hover:border-[rgba(208,48,69,.5)]',
                error ? 'border-acc' : 'border-line2',
              )}
            >
              Choose file from computer
            </button>
          )}
          <div className="text-[12px] text-t4 mt-1.5">Up to 1 hour. Large files are compressed automatically before upload.</div>
        </div>

        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))' }}>
          <Field label="Title" htmlFor="studio-title" hint="Leave blank to use the file name.">
            <Input
              id="studio-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={file ? deriveTitle(file.name) : 'Song title'}
            />
          </Field>
          <Field label="Artist or author" optional htmlFor="studio-artist">
            <Input id="studio-artist" value={artist} onChange={(e) => setArtist(e.target.value)} placeholder="Who wrote or recorded it" />
          </Field>
          <Field label="Rehearsal date" htmlFor="studio-date">
            <DatePicker id="studio-date" value={rehearsal} onChange={setRehearsal} maxDate={new Date()} />
          </Field>
          <Field label="Key hint" optional htmlFor="studio-key">
            <Select
              id="studio-key"
              value={keyHint}
              onChange={setKeyHint}
              options={[{ value: '', label: 'Not sure', sub: "We'll detect it" }, ...KEY_HINTS.map((k) => ({ value: k, label: k }))]}
            />
          </Field>
        </div>

        {error && (
          <Banner tone="acc" role="alert">
            {error}
          </Banner>
        )}

        <p className="text-[12.5px] text-t4">Analysis runs in the background. You can close this and keep working; we'll notify you when it's ready.</p>

        <ModalActions onCancel={close} confirmLabel={isAtLimit ? 'Replace and analyze' : 'Analyze'} />
      </form>
    </Modal>
  );
};
